import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogIn, UserPlus, Eye, EyeOff } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/api";
import { ASSO, BUREAU_ROLES, greetingFor } from "@/constants";

export default function Login({ bureau = false }) {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [showLoginPwd, setShowLoginPwd] = useState(false);
  const [showRegPwd, setShowRegPwd] = useState(false);
  const [registerForm, setRegisterForm] = useState({ first_name: "", last_name: "", email: "", phone: "", password: "", bureau_role: "", membership_number: "", directory_consent: false });
  const [loading, setLoading] = useState(false);
  const [verifyEmail, setVerifyEmail] = useState("");
  const [code, setCode] = useState("");

  const redirectFor = (user) => {
    const isAdminUser = ["admin", "super_admin"].includes(user.role);
    if (bureau) {
      if (isAdminUser) {
        navigate("/admin");
      } else {
        toast.error("Ce compte n'est pas membre du bureau. Redirection vers l'espace adhérent.");
        navigate("/espace");
      }
      return;
    }
    if (isAdminUser) {
      toast.info("Compte du bureau détecté — redirection vers l'espace bureau.");
      navigate("/admin");
      return;
    }
    navigate("/espace");
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(loginForm.email, loginForm.password);
      toast.success(`${greetingFor(user)} !`);
      redirectFor(user);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(registerForm);
      setVerifyEmail(registerForm.email);
      setCode("");
      toast.success("Compte créé ! Un code à 6 caractères vient d'être envoyé à votre email.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/verify-email", { email: verifyEmail, code: code.trim().toUpperCase() });
      toast.success("Email confirmé ! Votre demande d'adhésion a été transmise au bureau pour validation.");
      setVerifyEmail("");
      setCode("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    try {
      await api.post("/auth/resend-code", { email: verifyEmail });
      toast.success("Nouveau code envoyé (pensez à vérifier vos spams).");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="py-12 sm:py-16 lg:py-20 flex justify-center" data-testid="login-page">
      <div className="w-full max-w-md px-4">
        <div className="text-center mb-8 space-y-3">
          <div className="flex items-center justify-center gap-3 group">
            <img src={ASSO.logoTreeUrl} alt="" className="h-14 w-auto object-contain logo-tree" />
            <span className="leading-tight text-left">
              <span className="block font-serif text-slate-800">Cœur d'École – Toury Famille</span>
              <span className="block font-serif text-sm text-slate-500">Association — CETF —</span>
            </span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" data-testid="login-title">
            {bureau ? "Espace bureau" : "Espace adhérent"}
          </h1>
          <p className="text-sm text-slate-500">
            {bureau
              ? "Connexion réservée aux membres du bureau de l'association."
              : "Comptes rendus, documents internes et événements réservés."}
          </p>
        </div>
        {verifyEmail ? (
          <Card className="border-sky-200 shadow-lg" data-testid="verify-card">
            <CardContent className="p-6 space-y-4">
              <div className="text-center space-y-1">
                <h2 className="text-xl font-bold text-slate-900">Confirmez votre email</h2>
                <p className="text-sm text-slate-500 leading-relaxed">
                  Un code à 6 caractères vient d'être envoyé à <strong>{verifyEmail}</strong>.
                  Saisissez-le ci-dessous (pensez à vérifier vos spams).
                </p>
              </div>
              <form onSubmit={handleVerify} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="verify-code">Code de confirmation</Label>
                  <Input id="verify-code" data-testid="verify-code-input" required minLength={6} maxLength={6}
                    className="text-center text-2xl font-bold tracking-[0.4em] uppercase"
                    placeholder="••••••" value={code} onChange={(e) => setCode(e.target.value)} />
                </div>
                <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700" disabled={loading} data-testid="verify-submit-btn">
                  {loading ? "Vérification…" : "Confirmer mon identité"}
                </Button>
              </form>
              <p className="text-xs text-center text-slate-500 leading-relaxed">
                Une fois confirmé, votre compte sera transmis au bureau pour validation de votre adhésion.
              </p>
              <div className="flex items-center justify-between text-sm">
                <button type="button" onClick={handleResend} className="text-sky-700 hover:underline" data-testid="verify-resend-btn">
                  Renvoyer le code
                </button>
                <button type="button" onClick={() => setVerifyEmail("")} className="text-slate-400 hover:underline" data-testid="verify-back-btn">
                  Retour
                </button>
              </div>
            </CardContent>
          </Card>
        ) : (
        <Card className="border-slate-200">
          <CardContent className="p-6">
            <Tabs defaultValue="login">
              {!bureau && (
                <TabsList className="grid grid-cols-2 w-full mb-6">
                  <TabsTrigger value="login" data-testid="tab-login">Connexion</TabsTrigger>
                  <TabsTrigger value="register" data-testid="tab-register">Inscription</TabsTrigger>
                </TabsList>
              )}
              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email">Email</Label>
                    <Input id="login-email" data-testid="login-email-input" type="email" required autoComplete="email"
                      value={loginForm.email} onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="login-password">Mot de passe</Label>
                    <div className="relative">
                      <Input id="login-password" data-testid="login-password-input" type={showLoginPwd ? "text" : "password"} required autoComplete="current-password"
                        className="pr-10"
                        value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} />
                      <button type="button" onClick={() => setShowLoginPwd(!showLoginPwd)} data-testid="login-password-toggle"
                        aria-label={showLoginPwd ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors">
                        {showLoginPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <Button type="submit" className="w-full bg-sky-700 hover:bg-sky-800" disabled={loading} data-testid="login-submit-btn">
                    <LogIn className="h-4 w-4 mr-2" />{loading ? "Connexion…" : "Se connecter"}
                  </Button>
                  <p className="text-center">
                    <Link to="/mot-de-passe-oublie" className="text-sm text-sky-700 hover:underline" data-testid="forgot-password-link">
                      Mot de passe oublié ?
                    </Link>
                  </p>
                  {bureau && (
                    <p className="text-center text-xs text-slate-500 bg-slate-50 border border-slate-100 rounded-lg p-2.5 leading-relaxed">
                      Pas encore de compte ? L'inscription (fonction au sein du bureau + n° HelloAsso) se fait depuis{" "}
                      <Link to="/connexion" className="text-sky-700 font-medium hover:underline" data-testid="bureau-register-link">
                        l'Espace adhérent
                      </Link>.
                    </p>
                  )}
                </form>
              </TabsContent>
              <TabsContent value="register">
                {!bureau && (
                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="reg-firstname">Prénom</Label>
                      <Input id="reg-firstname" data-testid="register-firstname-input" required
                        value={registerForm.first_name} onChange={(e) => setRegisterForm({ ...registerForm, first_name: e.target.value })} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="reg-lastname">Nom</Label>
                      <Input id="reg-lastname" data-testid="register-lastname-input" required
                        value={registerForm.last_name} onChange={(e) => setRegisterForm({ ...registerForm, last_name: e.target.value })} />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="reg-email">Email</Label>
                    <Input id="reg-email" data-testid="register-email-input" type="email" required autoComplete="email"
                      value={registerForm.email} onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="reg-phone">Téléphone (optionnel)</Label>
                    <Input id="reg-phone" data-testid="register-phone-input" type="tel"
                      value={registerForm.phone} onChange={(e) => setRegisterForm({ ...registerForm, phone: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="reg-password">Mot de passe (8 caractères min.)</Label>
                    <div className="relative">
                      <Input id="reg-password" data-testid="register-password-input" type={showRegPwd ? "text" : "password"} required minLength={8} autoComplete="new-password"
                        className="pr-10"
                        value={registerForm.password} onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })} />
                      <button type="button" onClick={() => setShowRegPwd(!showRegPwd)} data-testid="register-password-toggle"
                        aria-label={showRegPwd ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors">
                        {showRegPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="reg-bureau-role">
                      Fonction au sein du bureau <span className="text-slate-400 font-normal">(uniquement si vous êtes membre du bureau)</span>
                    </Label>
                    <Select value={registerForm.bureau_role || "none"} onValueChange={(v) => setRegisterForm({ ...registerForm, bureau_role: v === "none" ? "" : v })}>
                      <SelectTrigger id="reg-bureau-role" data-testid="register-bureau-role-select"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">—</SelectItem>
                        {Object.entries(BUREAU_ROLES).filter(([k]) => k !== "").map(([k, label]) => (
                          <SelectItem key={k} value={k}>{label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="reg-membership-number">N° d'adhérent *</Label>
                    <Input id="reg-membership-number" data-testid="register-membership-number-input" required minLength={3} maxLength={40}
                      placeholder="Ex. : HA-2026-123456"
                      value={registerForm.membership_number} onChange={(e) => setRegisterForm({ ...registerForm, membership_number: e.target.value })} />
                    <p className="text-xs text-slate-500 leading-relaxed bg-sky-50 border border-sky-100 rounded-lg p-2.5">
                      Vous trouverez ce numéro dans le mail de confirmation HelloAsso attestant votre qualité de membre :
                      c'est la <strong>référence de commande</strong>.
                    </p>
                  </div>
                  <div className="flex items-start gap-3 rounded-lg bg-slate-50 p-3">
                    <Checkbox id="reg-consent" data-testid="register-consent-checkbox"
                      checked={registerForm.directory_consent}
                      onCheckedChange={(v) => setRegisterForm({ ...registerForm, directory_consent: !!v })} />
                    <Label htmlFor="reg-consent" className="text-xs leading-relaxed text-slate-600 font-normal cursor-pointer">
                      J'accepte que mes coordonnées figurent dans l'annuaire interne des bénévoles,
                      accessible uniquement au bureau de l'association (consentement RGPD, retirable à tout moment).
                    </Label>
                  </div>
                  <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700" disabled={loading} data-testid="register-submit-btn">
                    <UserPlus className="h-4 w-4 mr-2" />{loading ? "Création…" : "Créer mon compte"}
                  </Button>
                  <p className="text-xs text-center text-slate-500">
                    Votre compte sera activé par le bureau après vérification de votre adhésion.
                  </p>
                </form>
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
        )}
      </div>
    </div>
  );
}
